import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Swal from "sweetalert2";
import {
  fetchGHServiceDetails,
  updateGHStayInfo,
  deleteGHSingleService,
  deleteGHPayment,
  deleteGHRecord,
  GH_STATUSES,
} from "../../../services/guestHouseServicesApi";
import AddServicesModal from "./AddServicesModal";
import EditServiceItemModal from "./EditServiceItemModal";
import AddPaymentModal from "./AddPaymentModal";
import EditPaymentModal from "./EditPaymentModal";
import EditStayInfoModal from "./EditStayInfoModal";

export default function GHDetailsModal({
  open,
  onClose,
  recordId,
  onRecordUpdated,
}) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("services"); // "services" | "payments" | "notes"

  // Sub-modals
  const [addServicesOpen, setAddServicesOpen] = useState(false);
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [selectedServiceItem, setSelectedServiceItem] = useState(null);
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const [editPaymentOpen, setEditPaymentOpen] = useState(false);
  const [selectedPaymentItem, setSelectedPaymentItem] = useState(null);
  const [editStayOpen, setEditStayOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (open && recordId) {
      loadDetails();
    }
  }, [open, recordId]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const data = await fetchGHServiceDetails(recordId);
      setRecord(data);
    } catch (err) {
      console.error("Error loading GH service details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!record || record.status === newStatus) return;
    setUpdatingStatus(true);
    try {
      await updateGHStayInfo(record._id || record.guestHouseServiceId, {
        status: newStatus,
      });
      Swal.fire({
        title: "Status Changed",
        text: `Booking status changed to ${newStatus}`,
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });
      loadDetails();
      if (onRecordUpdated) onRecordUpdated();
    } catch (err) {
      Swal.fire("Error", typeof err === "string" ? err : "Failed to change status", "error");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteServiceItem = async (svc) => {
    const result = await Swal.fire({
      title: "Remove Service Item?",
      text: `Are you sure you want to remove "${svc.serviceName}"? Billing totals will recalculate.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Delete",
    });

    if (result.isConfirmed) {
      try {
        await deleteGHSingleService(
          record._id || record.guestHouseServiceId,
          svc._id || svc.serviceId
        );
        Swal.fire("Deleted", "Service item removed successfully.", "success");
        loadDetails();
        if (onRecordUpdated) onRecordUpdated();
      } catch (err) {
        Swal.fire("Error", typeof err === "string" ? err : "Failed to remove item", "error");
      }
    }
  };

  const handleDeletePayment = async (pay) => {
    const result = await Swal.fire({
      title: "Delete Payment Entry?",
      text: `Are you sure you want to delete payment of ₹${pay.paid_amount}? Outstanding balance will update.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Delete",
    });

    if (result.isConfirmed) {
      try {
        await deleteGHPayment(record._id || record.guestHouseServiceId, pay._id);
        Swal.fire("Deleted", "Payment entry deleted successfully.", "success");
        loadDetails();
        if (onRecordUpdated) onRecordUpdated();
      } catch (err) {
        Swal.fire("Error", typeof err === "string" ? err : "Failed to delete payment", "error");
      }
    }
  };

  const handleDeleteRecord = async () => {
    const result = await Swal.fire({
      title: "Delete Entire Booking Record?",
      text: `This will permanently delete ${record?.guestHouseServiceId} and all associated services & payment logs.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Delete Record",
    });

    if (result.isConfirmed) {
      try {
        await deleteGHRecord(record._id || record.guestHouseServiceId);
        Swal.fire("Deleted", "Guest house service record deleted.", "success");
        if (onRecordUpdated) onRecordUpdated();
        onClose();
      } catch (err) {
        Swal.fire("Error", typeof err === "string" ? err : "Failed to delete record", "error");
      }
    }
  };

  if (!open) return null;

  const totalBill = Number(record?.final_amount || record?.totalAmount || 0);
  const paidBill = Number(record?.paidAmount || 0);
  const dueBill = Number(record?.dueAmount || Math.max(0, totalBill - paidBill));

  const stayFrom =
    record?.stayDateRangeFrom ||
    (record?.stayDateRange?.from ? new Date(record.stayDateRange.from).toLocaleDateString("en-GB") : "-");
  const stayTo =
    record?.stayDateRangeTo ||
    (record?.stayDateRange?.to ? new Date(record.stayDateRange.to).toLocaleDateString("en-GB") : "-");

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        className="gh-modal-dialog gh-modal-lg"
        scroll="paper"
        disableAutoFocus
        disableRestoreFocus
        PaperProps={{
          style: {
            borderRadius: "16px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            maxHeight: "calc(100vh - 48px)",
            height: "min(880px, calc(100vh - 48px))",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.35)",
          },
        }}
      >
        {/* Sticky Header */}
        <div
          className="gh-modal-header"
          style={{
            background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fa fa-building fs-5" />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 fw-bold" style={{ fontSize: "18px" }}>
                  {record?.guestHouseServiceId || "Service Record"}
                </h5>
                <span className="badge bg-light text-dark fw-bold">
                  {record?.status || "Active"}
                </span>
              </div>
              <small style={{ color: "rgba(255,255,255,0.85)", fontSize: "13px" }}>
                Patient: <strong>{record?.patient_name}</strong> ({record?.patientId})
                {record?.treatment_id && ` • Treatment: ${record.treatment_id}`}
              </small>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-success d-flex align-items-center gap-1 shadow-sm fw-bold"
              onClick={() => setAddPaymentOpen(true)}
            >
              <i className="fa fa-credit-card" /> Add Payment
            </button>
            <button
              type="button"
              className="btn btn-sm btn-light d-flex align-items-center gap-1 shadow-sm fw-bold text-primary"
              onClick={() => setAddServicesOpen(true)}
            >
              <i className="fa fa-plus-circle" /> Add Services
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                color: "#fff",
                fontSize: "20px",
                cursor: "pointer",
              }}
            >
              <i className="fa fa-times" />
            </button>
          </div>
        </div>

        <DialogContent
          style={{
            padding: "20px 24px",
            backgroundColor: "#f8fafc",
            flex: "1 1 auto",
            overflowY: "auto",
          }}
        >
          {loading && !record ? (
            <div className="text-center py-5">
              <i className="fa fa-spinner fa-spin fa-2x text-primary mb-2" />
              <p className="text-muted">Loading service record details...</p>
            </div>
          ) : !record ? (
            <div className="text-center py-5 text-muted">Record not found</div>
          ) : (
            <div>
              {/* Top Overview Cards */}
              <div className="row g-3 mb-3">
                {/* Stay Info Card */}
                <div className="col-md-5">
                  <div className="card border-0 shadow-sm p-3 h-100 bg-white" style={{ borderRadius: "10px" }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <span className="small text-muted d-block">Property & Room</span>
                        <h6 className="fw-bold text-dark mb-0 fs-6">
                          {record.guestHouseName || "Guest House"}
                        </h6>
                        <span className="badge bg-light text-muted border mt-1">
                          Room: <strong>{record.roomNumber || "Not Assigned"}</strong>
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm py-0 px-2"
                        style={{ fontSize: "11.5px" }}
                        onClick={() => setEditStayOpen(true)}
                      >
                        <i className="fa fa-pencil me-1" /> Edit Stay
                      </button>
                    </div>

                    <div className="pt-2 border-top small text-muted">
                      <div>
                        <i className="fa fa-calendar me-1 text-primary" /> Stay Period:{" "}
                        <strong className="text-dark">
                          {stayFrom} → {stayTo}
                        </strong>
                      </div>
                      {record.createdAt && (
                        <div className="mt-1">
                          <i className="fa fa-clock-o me-1 text-secondary" /> Booked on:{" "}
                          {new Date(record.createdAt).toLocaleString("en-GB")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Financial Summary Card */}
                <div className="col-md-7">
                  <div className="card border-0 shadow-sm p-3 h-100 bg-white" style={{ borderRadius: "10px" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="small fw-semibold text-dark">Billing & Payment Summary</span>
                      <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Status:</span>
                        <select
                          className="form-select form-select-sm"
                          style={{ width: "auto", fontSize: "12px", fontWeight: 600 }}
                          value={record.status}
                          onChange={(e) => handleStatusChange(e.target.value)}
                          disabled={updatingStatus}
                        >
                          {GH_STATUSES.map((st) => (
                            <option key={st} value={st}>
                              {st}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="row g-2 text-center pt-2">
                      <div className="col-4">
                        <div className="p-2 rounded bg-light border">
                          <small className="text-muted d-block" style={{ fontSize: "11px" }}>Total Billed</small>
                          <strong className="fs-6 text-dark">₹{totalBill.toLocaleString()}</strong>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="p-2 rounded bg-light border">
                          <small className="text-muted d-block" style={{ fontSize: "11px" }}>Total Received</small>
                          <strong className="fs-6 text-success">₹{paidBill.toLocaleString()}</strong>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="p-2 rounded bg-light border">
                          <small className="text-muted d-block" style={{ fontSize: "11px" }}>Net Due</small>
                          <strong className={`fs-6 ${dueBill > 0 ? "text-danger" : "text-secondary"}`}>
                            ₹{dueBill.toLocaleString()}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Tabs */}
              <ul className="nav nav-pills mb-3 gap-2">
                <li className="nav-item">
                  <button
                    className={`btn btn-sm ${
                      activeTab === "services" ? "btn-primary shadow-sm" : "btn-white border"
                    }`}
                    style={{ borderRadius: "8px", fontWeight: 600, padding: "6px 16px" }}
                    onClick={() => setActiveTab("services")}
                  >
                    <i className="fa fa-list-check me-1" /> Services ({record.services?.length || 0})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`btn btn-sm ${
                      activeTab === "payments" ? "btn-primary shadow-sm" : "btn-white border"
                    }`}
                    style={{ borderRadius: "8px", fontWeight: 600, padding: "6px 16px" }}
                    onClick={() => setActiveTab("payments")}
                  >
                    <i className="fa fa-credit-card me-1" /> Payment Receipts ({record.payments?.length || 0})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`btn btn-sm ${
                      activeTab === "notes" ? "btn-primary shadow-sm" : "btn-white border"
                    }`}
                    style={{ borderRadius: "8px", fontWeight: 600, padding: "6px 16px" }}
                    onClick={() => setActiveTab("notes")}
                  >
                    <i className="fa fa-sticky-note me-1" /> Remarks & Notes
                  </button>
                </li>
              </ul>

              {/* Tab 1: Services List */}
              {activeTab === "services" && (
                <div className="card border-0 shadow-sm bg-white" style={{ borderRadius: "10px" }}>
                  <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: "14px" }}>
                      Services & Amenities Provided
                    </h6>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                      onClick={() => setAddServicesOpen(true)}
                    >
                      <i className="fa fa-plus" /> Add Services
                    </button>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Service Name</th>
                          <th>Duration</th>
                          <th>Unit Price</th>
                          <th>Qty</th>
                          <th>Disc %</th>
                          <th>Final Price</th>
                          <th>Status</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!record.services || record.services.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center py-4 text-muted">
                              No services added to this booking yet.
                            </td>
                          </tr>
                        ) : (
                          record.services.map((svc, idx) => (
                            <tr key={svc._id || idx}>
                              <td className="text-muted">{idx + 1}</td>
                              <td>
                                <strong className="text-dark d-block">{svc.serviceName}</strong>
                                {svc.description && (
                                  <small className="text-muted">{svc.description}</small>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border">
                                  {svc.duration || "One-Time"}
                                </span>
                              </td>
                              <td>₹{(Number(svc.price) || 0).toLocaleString()}</td>
                              <td>{svc.quantity || 1}</td>
                              <td>{svc.discount_percentage ? `${svc.discount_percentage}%` : "-"}</td>
                              <td className="fw-bold text-success">
                                ₹{(Number(svc.final_price || svc.price * (svc.quantity || 1)) || 0).toLocaleString()}
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    svc.status === "Completed"
                                      ? "bg-success"
                                      : svc.status === "Cancelled"
                                      ? "bg-danger"
                                      : "bg-info text-white"
                                  }`}
                                  style={{ fontSize: "11px" }}
                                >
                                  {svc.status || "In Progress"}
                                </span>
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-sm text-primary p-1 me-1"
                                  onClick={() => {
                                    setSelectedServiceItem(svc);
                                    setEditItemModalOpen(true);
                                  }}
                                  title="Edit Service Item"
                                >
                                  <i className="fa fa-pencil" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm text-danger p-1"
                                  onClick={() => handleDeleteServiceItem(svc)}
                                  title="Delete Service Item"
                                >
                                  <i className="fa fa-trash" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tab 2: Payments List */}
              {activeTab === "payments" && (
                <div className="card border-0 shadow-sm bg-white" style={{ borderRadius: "10px" }}>
                  <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: "14px" }}>
                      Payment Receipts History
                    </h6>
                    <button
                      type="button"
                      className="btn btn-success btn-sm d-flex align-items-center gap-1"
                      onClick={() => setAddPaymentOpen(true)}
                    >
                      <i className="fa fa-plus" /> Record Payment
                    </button>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Payment Date</th>
                          <th>Amount Paid</th>
                          <th>Method</th>
                          <th>Status</th>
                          <th>Notes</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!record.payments || record.payments.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4 text-muted">
                              No payment receipts recorded yet.
                            </td>
                          </tr>
                        ) : (
                          record.payments.map((p, idx) => (
                            <tr key={p._id || idx}>
                              <td className="text-muted">{idx + 1}</td>
                              <td>{p.payment_Date || "-"}</td>
                              <td className="fw-bold text-success">
                                ₹{(Number(p.paid_amount) || 0).toLocaleString()}
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border">
                                  {p.paymentMethod || "Cash"}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-success" style={{ fontSize: "11px" }}>
                                  {p.status || "Approved"}
                                </span>
                              </td>
                              <td className="text-muted small" style={{ maxWidth: "250px" }}>
                                {p.notes || "-"}
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-sm text-primary p-1 me-1"
                                  onClick={() => {
                                    setSelectedPaymentItem(p);
                                    setEditPaymentOpen(true);
                                  }}
                                  title="Edit Payment"
                                >
                                  <i className="fa fa-pencil" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm text-danger p-1"
                                  onClick={() => handleDeletePayment(p)}
                                  title="Delete Payment"
                                >
                                  <i className="fa fa-trash" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Tab 3: Notes & Instructions */}
              {activeTab === "notes" && (
                <div className="card border-0 shadow-sm p-3 bg-white" style={{ borderRadius: "10px" }}>
                  <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "14px" }}>
                    Special Instructions & Stay Remarks
                  </h6>
                  <div className="p-3 bg-light rounded border text-muted">
                    {record.notes ? (
                      <p className="mb-0 text-dark">{record.notes}</p>
                    ) : (
                      <p className="mb-0 text-muted fst-italic">No special notes recorded for this stay.</p>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}
        </DialogContent>

        {/* Pinned Sticky Footer Actions */}
        {record && (
          <div
            className="gh-modal-footer"
            style={{
              padding: "12px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 -4px 12px rgba(0,0,0,0.04)",
            }}
          >
            <button
              type="button"
              className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
              onClick={handleDeleteRecord}
            >
              <i className="fa fa-trash" /> Delete Entire Record
            </button>

            <button
              type="button"
              className="btn btn-secondary btn-sm px-4"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        )}
      </Dialog>

      {/* Sub Modals */}
      <AddServicesModal
        open={addServicesOpen}
        onClose={() => setAddServicesOpen(false)}
        record={record}
        onSuccess={() => {
          loadDetails();
          if (onRecordUpdated) onRecordUpdated();
        }}
      />

      <EditServiceItemModal
        open={editItemModalOpen}
        onClose={() => {
          setEditItemModalOpen(false);
          setSelectedServiceItem(null);
        }}
        record={record}
        serviceItem={selectedServiceItem}
        onSuccess={() => {
          loadDetails();
          if (onRecordUpdated) onRecordUpdated();
        }}
      />

      <AddPaymentModal
        open={addPaymentOpen}
        onClose={() => setAddPaymentOpen(false)}
        record={record}
        onSuccess={() => {
          loadDetails();
          if (onRecordUpdated) onRecordUpdated();
        }}
      />

      <EditPaymentModal
        open={editPaymentOpen}
        onClose={() => {
          setEditPaymentOpen(false);
          setSelectedPaymentItem(null);
        }}
        record={record}
        paymentItem={selectedPaymentItem}
        onSuccess={() => {
          loadDetails();
          if (onRecordUpdated) onRecordUpdated();
        }}
      />

      <EditStayInfoModal
        open={editStayOpen}
        onClose={() => setEditStayOpen(false)}
        record={record}
        onSuccess={() => {
          loadDetails();
          if (onRecordUpdated) onRecordUpdated();
        }}
      />
    </>
  );
}
